// React
import { ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import { Placeholder } from '@utmb/design-system/placeholder/Placeholder';
// Contexts
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useUser } from '@utmb/contexts/AuthContext';
// Utils
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
// Interfaces
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
// Styles
import styles from './my-dashboard-header.module.scss';

import { IconEnum } from '@utmb/types/enum/IconEnum';
import { ArrowLink } from '../my-dashboard/ArrowLink';
import { MyUtmbLabel } from '../my-utmb/MyUtmbLabel';
import MyLoyaltyLevel from '../my-loyalty/MyLoyaltyLevel';
import { Flag } from '@utmb/design-system/flag/Flag';
import isMobile from '@utmb/utils/is-mobile';
import RunningStones from '@utmb/design-system/running-stone/RunningStones';
import { ColorTypesEnum } from '@utmb/types/colors';

export const MyDashboardMemberHeader = (): ReactElement | null => {
    const { dashboardMember } = useDashboard();
    const { me } = useUser();
    const { profilePicture } = me!;
    const { t } = useTranslation();

    if (!dashboardMember) {
        return null;
    }

    const { information, stones, percentFilled, isLegend, isElite } = dashboardMember;

    // Rebuilding URL to not open new browser tab
    //const runnerURL = information?.runnerPageUrl ? new URL(information.runnerPageUrl).pathname : null;
    //const anonymized = information?.anonymize;

    const generalIndex = dashboardMember?.performanceIndexesExpires?.find((index) => index.piCategory === PiCategoryEnum.GENERAL);
    const isCurrentLevel = typeof dashboardMember?.loyalty?.currentLevel === 'number' && dashboardMember.loyalty.levels[0].minPoints !== -1;

    let memberYear: string | null = information?.createdAt.slice(-4);

    if (!Number.isInteger(parseInt(memberYear))) {
        memberYear = null;
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.left}>
                    <div className={styles.profile_picture_container}>
                        {profilePicture ? (
                            <Image objectFit="cover" layout="fill" image={pIdToCloudinary(profilePicture, information.firstName)} />
                        ) : (
                            <Placeholder isInvert logoWidth={60} logoHeight={60} />
                        )}
                    </div>
                    <div className={styles.information_container}>
                        <Font.FuturaHeavy noTranslate className={styles.name} mobile="32" desktop="36">
                            {information.firstName}
                        </Font.FuturaHeavy>
                        <Font.FuturaHeavy noTranslate className={styles.surname} mobile="32" desktop="36">
                            {information.lastName}
                        </Font.FuturaHeavy>
                        {isCurrentLevel ? (
                            <div className={styles.loyaltylevel}>
                                <MyUtmbLabel forcePlus />
                                <MyLoyaltyLevel className={styles.loyalty} />
                            </div>
                        ) : null}
                        <div className={styles.additionnal_information}>
                            <div>
                                <Flag isoCode={dashboardMember.information.nationality} hideShadow className={styles.flag} />
                                <div>{dashboardMember.information.nationalityName}</div>
                            </div>
                            <div className={styles.categ}>{dashboardMember.information.category}</div>
                            {isLegend && (
                                <div className={styles.legend}>
                                    <Icon variant={IconEnum.UTMB_RUNNER} color="#e9c118" clss={styles.icon} />
                                    LEGEND RUNNER
                                </div>
                            )}
                            {isElite && (
                                <div className={styles.elite}>
                                    <Icon variant={IconEnum.LOGO_SIMPLIFIED_DARK} clss={styles.icon} />
                                    ELITE MEMBER
                                </div>
                            )}
                        </div>
                        <div className={styles.completion}>
                            <Link href="my-information" className={styles.pct}>
                                {t('myutmb:myUtmb.myDashboard.percent', { percent: percentFilled })}
                                <Icon variant={IconEnum.PEN} color="white" clss={styles.icon} />
                            </Link>
                        </div>
                    </div>
                </div>
                <div className={styles.status_container}>
                    <div className={styles.index}>
                        <Font.OxaniumBold mobile="50" desktop="60" as="p">
                            {generalIndex?.index || 0}
                        </Font.OxaniumBold>
                        <PiCategoryLogo piCategory={PiCategoryEnum.GENERAL} height={isMobile() ? 23 : 26} />
                        <ArrowLink label={t(`myutmb:myUtmb.myDashboard.achievementsBtn`)} url="/my-utmb/my-achievements" className={styles.link} />
                    </div>
                    <div className={styles.running_stones}>
                        <div className={styles.wrapStones}>
                            <RunningStones quantity={stones.quantity} size={75} color={ColorTypesEnum.SECONDARY} />
                        </div>
                        <ArrowLink label={t(`myutmb:myUtmb.myDashboard.finalsBtn`)} url="/my-utmb/my-road-to-utmb" className={styles.link} />
                    </div>
                </div>
            </div>
        </>
    );
};
